import { cardHeight } from './card-height'
import { cardExtensions, cardField, cardSchema } from './card-schema'
import { type JSONContent } from '@tiptap/core'
import { MarkdownManager } from '@tiptap/markdown'
import { Node, type Node as PMNode } from '@tiptap/pm/model'
import {
  prosemirrorJSONToYXmlFragment,
  updateYFragment,
  yXmlFragmentToProseMirrorRootNode,
} from '@tiptap/y-tiptap'
import type * as Y from 'yjs'

// Card text as markdown, converted to and from the card's Y.XmlFragment: how
// the AI reads and writes cards, and how the browser makes a card from text
// (a message turned into a card). Writes go through `updateYFragment`, which applies the
// smallest change, so a person typing in the same card merges cleanly.

const markdown = new MarkdownManager({ extensions: cardExtensions() })

export class CardEditError extends Error {}

function rootNode(doc: Y.Doc, id: string): PMNode | null {
  const frag = doc.getXmlFragment(cardField(id))
  if (frag.length === 0) return null
  return yXmlFragmentToProseMirrorRootNode(frag, cardSchema)
}

export function readCard(doc: Y.Doc, id: string): string {
  const node = rootNode(doc, id)
  return node ? unescapeText(markdown.serialize(node.toJSON() as JSONContent).trim()) : ''
}

/**
 * The serializer writes `<`, `>` and `&` in text as HTML entities. The card
 * holds the characters themselves, and parsing keeps tag-like text as text, so
 * markdown with the characters writes back the same card: show those. Without
 * this, "差 >20 mmHg" read back as "差 &gt;20 mmHg" and every write of a `<`
 * or `>` was reported to the AI as lost. Code is left alone (it is never
 * escaped). The one thing that does not survive: text that literally reads
 * "&lt;" becomes "<" when the card is next written.
 */
function unescapeText(md: string): string {
  return md
    .split(/(```[\s\S]*?```|`[^`\n]*`)/)
    .map((part, i) =>
      i % 2 ? part : part.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&'),
    )
    .join('')
}

/** The height card `id` is drawn at `w` wide (shared/card-height.ts), from its stored text. */
export function cardTextHeight(doc: Y.Doc, id: string, w: number): number {
  return cardHeight(rootNode(doc, id), w)
}

/**
 * Blocks taken out of a card (dragged onto the board), as the markdown of a
 * new card: a heading among them leads as its title, else the title is empty.
 */
export function blocksToCardMarkdown(blocks: JSONContent[]): string {
  const [first, ...rest] = blocks
  const lead =
    first?.type === 'heading'
      ? [{ ...first, attrs: { ...first.attrs, level: 1 } }, ...rest]
      : [{ type: 'heading', attrs: { level: 1 } }, ...blocks]
  const md = markdown.serialize({ type: 'doc', content: lead }).trim()
  return unescapeText(md)
}

/** First heading's text, or '' for an empty card. */
export function cardTitle(doc: Y.Doc, id: string): string {
  const first = rootNode(doc, id)?.firstChild
  return first?.type.name === 'heading' ? first.textContent : ''
}

export function parseCard(md: string): PMNode {
  // A card holds no images (an image is its own item on the board), and the
  // schema would throw on one: keep it as a link to the same address.
  let json = markdown.parse(md.replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, '[$1]($2)'))
  // The schema requires a leading heading: give untitled text an empty one.
  if (json.content?.[0]?.type !== 'heading') {
    json = { ...json, content: [{ type: 'heading', attrs: { level: 1 } }, ...(json.content ?? [])] }
  }
  return Node.fromJSON(cardSchema, json)
}

/**
 * The content of markdown with its formatting stripped: whitespace and markdown
 * punctuation removed, so re-serialisation (table padding, list markers,
 * escaping) does not count as a change but dropped words do.
 */
const normalise = (md: string) => md.replace(/[\s|\-*_#`>~\\[\]()!:+=.]/g, '')

/**
 * Markdown the card schema cannot hold is dropped on write. Report it instead
 * of losing it silently: returns null when the card reads back as written.
 */
export function writeLoss(written: string, readBack: string): string | null {
  if (normalise(written) === normalise(readBack)) return null
  return `Some of what you wrote could not be stored as written. The card now reads:\n${readBack}`
}

/** Replace the whole card with `md`. `origin` tags the Y transaction (who wrote it). */
export function writeCard(doc: Y.Doc, id: string, md: string, origin: unknown) {
  const frag = doc.getXmlFragment(cardField(id))
  const node = parseCard(md)
  doc.transact(() => {
    if (frag.length === 0) prosemirrorJSONToYXmlFragment(cardSchema, node.toJSON(), frag)
    else updateYFragment(doc, frag, node, { mapping: new Map(), isOMark: new Map() })
  }, origin)
}

/**
 * Replace exactly one occurrence of `oldText` in the card's markdown with
 * `newText`, like a text editor's find-and-replace. Fails if `oldText` is
 * missing or ambiguous, so the caller can re-read and retry.
 */
export function editCard(
  doc: Y.Doc,
  id: string,
  oldText: string,
  newText: string,
  origin: unknown,
) {
  const current = readCard(doc, id)
  if (!oldText) throw new CardEditError('old text is empty')
  const span = findOnce(current, oldText) ?? findOnce(current, oldText, true)
  if (!span) throw new CardEditError('old text not found in the card')
  if (span === 'many')
    throw new CardEditError('old text appears more than once; include more context')
  writeCard(doc, id, current.slice(0, span.from) + newText + current.slice(span.to), origin)
}

/**
 * Where `text` is in `md`, exactly once. `loose`: as the caller would have
 * written it before the card stored it, i.e. with other spacing (tables are
 * padded, lines rewrapped) and without the backslashes markdown adds before
 * punctuation (`\_`, `\*`).
 */
function findOnce(
  md: string,
  text: string,
  loose = false,
): { from: number; to: number } | 'many' | null {
  if (!loose) {
    const at = md.indexOf(text)
    if (at < 0) return null
    return md.indexOf(text, at + 1) >= 0 ? 'many' : { from: at, to: at + text.length }
  }
  const hay = squeeze(md)
  const needle = squeeze(text).text
  if (!needle) return null
  const at = hay.text.indexOf(needle)
  if (at < 0) return null
  if (hay.text.indexOf(needle, at + 1) >= 0) return 'many'
  const from = hay.at[at] ?? 0
  const to = (hay.at[at + needle.length - 1] ?? from) + 1
  return { from, to }
}

/** `s` without whitespace and without escaping backslashes, with where each kept character was. */
function squeeze(s: string): { text: string; at: number[] } {
  let text = ''
  const at: number[] = []
  for (let i = 0; i < s.length; i++) {
    const c = s.charAt(i)
    if (/\s/.test(c)) continue
    if (c === '\\' && /[!-/:-@[-`{-~]/.test(s.charAt(i + 1))) continue
    text += c
    at.push(i)
  }
  return { text, at }
}
